// JSM import wizard: reuses the existing Jira connection → pick a Service
// Management project → preview → import into a brand-new Service Desk portal
// (with live progress). If no Jira connection exists yet, the user is pointed
// to the Projects "Import from Jira" wizard to connect first.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Plugs, CircleNotch, MagnifyingGlass, CheckCircle, WarningCircle, ArrowLeft,
  ArrowRight, Lifebuoy, UsersThree, ListChecks, DownloadSimple,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function JsmImportDialog({ open, onClose, onOpenPortal }) {
  const { API, getOrgHeaders } = useAuth();
  const cfg = useCallback(() => ({ withCredentials: true, headers: getOrgHeaders() }), [getOrgHeaders]);

  const [step, setStep] = useState('loading'); // loading | connect | projects | preview | progress
  const [conn, setConn] = useState(null);
  const [form, setForm] = useState({ site_url: '', email: '', api_token: '' });
  const [connecting, setConnecting] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  const loadProjects = useCallback(async () => {
    setLoadingProjects(true);
    try {
      const { data } = await axios.get(`${API}/jsm/projects`, cfg());
      setProjects(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load JSM projects'); }
    finally { setLoadingProjects(false); }
  }, [API, cfg]);

  useEffect(() => {
    if (!open) return;
    setStep('loading'); setSelected(null); setPreview(null); setJob(null); setSearch('');
    (async () => {
      try {
        const { data } = await axios.get(`${API}/jsm/connection`, cfg());
        if (data.connected) { setConn(data); setStep('projects'); loadProjects(); }
        else { setConn(null); setStep('connect'); }
      } catch { setConn(null); setStep('connect'); }
    })();
  }, [open, API, cfg, loadProjects]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const connect = async () => {
    if (!form.site_url || !form.email || !form.api_token) { toast.error('Site URL, email and API token are required'); return; }
    setConnecting(true);
    try {
      const { data } = await axios.post(`${API}/jsm/connection`, form, cfg());
      setConn(data); setForm({ site_url: '', email: '', api_token: '' });
      toast.success(`Connected as ${data.verified_as || data.display_name}`);
      setStep('projects'); loadProjects();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not connect to Jira'); }
    finally { setConnecting(false); }
  };

  const disconnect = async () => {
    try { await axios.delete(`${API}/jsm/connection`, cfg()); } catch { /* ignore */ }
    setConn(null); setProjects([]); setStep('connect');
  };

  const pickProject = async (p) => {
    setSelected(p); setLoadingPreview(true); setStep('preview'); setPreview(null);
    try {
      const { data } = await axios.post(`${API}/jsm/preview`, { project_key: p.key }, cfg());
      setPreview(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Preview failed'); setStep('projects'); }
    finally { setLoadingPreview(false); }
  };

  const startImport = async () => {
    try {
      const { data } = await axios.post(`${API}/jsm/import`, { project_key: selected.key, project_name: selected.name }, cfg());
      setStep('progress');
      setJob({ status: 'queued' });
      pollRef.current = setInterval(async () => {
        try {
          const { data: j } = await axios.get(`${API}/jsm/import/${data.job_id}`, cfg());
          setJob(j);
          if (j.status === 'done' || j.status === 'error') { clearInterval(pollRef.current); pollRef.current = null; }
        } catch { /* keep polling */ }
      }, 1500);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not start import'); }
  };

  const close = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } onClose(); };

  const filtered = projects.filter(p => `${p.name} ${p.key}`.toLowerCase().includes(search.toLowerCase()));
  const total = job?.total || 0;
  const processed = job?.processed || 0;
  const fetched = job?.fetched || 0;
  const pct = job?.status === 'done' ? 100 : (total ? Math.round((processed / total) * 100) : (fetched ? 35 : 10));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="!w-[720px] !max-w-[95vw] max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="jsm-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="w-6 h-6 rounded bg-[#0052CC] flex items-center justify-center"><Lifebuoy size={14} weight="fill" className="text-white" /></span>
            Import from Jira Service Management
          </DialogTitle>
        </DialogHeader>

        {step === 'loading' && (
          <div className="py-12 text-center text-zinc-400" data-testid="jsm-step-loading"><CircleNotch size={24} className="animate-spin mx-auto" /></div>
        )}

        {/* Connect a Service Management Jira site (dedicated, separate from Projects) */}
        {step === 'connect' && (
          <div className="space-y-3 py-1" data-testid="jsm-step-connect">
            <p className="text-xs text-zinc-500 -mt-1">Connect the Jira <span className="font-medium text-zinc-700">Service Management</span> site you want to import requests from. This connection is kept separate from the Projects-board Jira importer.</p>
            <div className="space-y-2.5">
              <div>
                <label className="text-[11px] font-semibold text-zinc-600 mb-1 block">Jira site URL</label>
                <Input placeholder="https://yourcompany.atlassian.net" value={form.site_url} onChange={(e) => setForm(f => ({ ...f, site_url: e.target.value }))} data-testid="jsm-connect-site" />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-zinc-600 mb-1 block">Atlassian account email</label>
                <Input placeholder="you@company.com" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} data-testid="jsm-connect-email" />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-zinc-600 mb-1 block">API token</label>
                <Input type="password" placeholder="Paste your Atlassian API token" value={form.api_token} onChange={(e) => setForm(f => ({ ...f, api_token: e.target.value }))} data-testid="jsm-connect-token" />
                <p className="text-[10px] text-zinc-400 mt-1">Create one at <span className="font-medium">id.atlassian.com → Security → API tokens</span>. Stored encrypted.</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={close}>Cancel</Button>
              <Button onClick={connect} disabled={connecting} data-testid="jsm-connect-submit">
                {connecting ? <><CircleNotch size={15} className="animate-spin mr-1.5" /> Verifying…</> : <><Plugs size={15} className="mr-1.5" /> Connect</>}
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Choose a JSM project */}
        {step === 'projects' && (
          <div className="space-y-3 py-1" data-testid="jsm-step-projects">
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs text-zinc-500">Connected as <span className="font-medium text-zinc-700">{conn?.display_name}</span> · {conn?.site_url?.replace('https://', '')}</div>
              <button onClick={disconnect} className="text-[11px] text-zinc-400 hover:text-red-500 underline shrink-0" data-testid="jsm-disconnect">Use a different account</button>
            </div>
            <div className="relative">
              <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input className="pl-9" placeholder="Search Service Management projects…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="jsm-project-search" />
            </div>
            <div className="max-h-[340px] overflow-y-auto space-y-1.5">
              {loadingProjects ? (
                <div className="py-10 text-center text-zinc-400"><CircleNotch size={22} className="animate-spin mx-auto" /></div>
              ) : filtered.length === 0 ? (
                <p className="py-8 text-center text-sm text-zinc-400">No Service Management projects found on this Jira site.</p>
              ) : filtered.map(p => (
                <button key={p.id} onClick={() => pickProject(p)} data-testid={`jsm-project-${p.key}`}
                  className="w-full flex items-center gap-3 p-2.5 rounded-lg border border-zinc-200 hover:border-blue-400 hover:bg-blue-50/40 text-left transition-colors">
                  {p.avatar ? <img src={p.avatar} alt="" className="w-7 h-7 rounded" /> : <span className="w-7 h-7 rounded bg-zinc-200 flex items-center justify-center text-[10px] font-bold text-zinc-600">{p.key?.slice(0, 2)}</span>}
                  <div className="min-w-0 flex-1"><p className="text-sm font-medium text-zinc-900 truncate">{p.name}</p><p className="text-[11px] text-zinc-400">{p.key}</p></div>
                  <ArrowRight size={15} className="text-zinc-300" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Preview */}
        {step === 'preview' && (
          <div className="space-y-4 py-1 min-w-0 w-full" data-testid="jsm-step-preview">
            <button onClick={() => setStep('projects')} className="text-xs text-zinc-500 hover:text-zinc-800 flex items-center gap-1"><ArrowLeft size={13} /> Back to projects</button>
            {loadingPreview || !preview ? (
              <div className="py-12 text-center text-zinc-400"><CircleNotch size={24} className="animate-spin mx-auto mb-2" /><p className="text-sm">Analyzing {selected?.name}…</p></div>
            ) : (
              <>
                <div className="overflow-y-auto overflow-x-hidden pr-1 space-y-4 min-w-0" style={{ maxHeight: '56vh' }}>
                  <div className="rounded-lg border border-zinc-200 p-4">
                    <p className="text-sm font-semibold text-zinc-900">{selected?.name} <span className="text-zinc-400 font-normal">({selected?.key})</span></p>
                    <p className="text-2xl font-bold text-zinc-900 font-['Outfit'] mt-1" data-testid="jsm-preview-count">{preview.count != null ? preview.count : preview.sampleCount}+ <span className="text-sm font-normal text-zinc-500">requests to import</span></p>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="rounded-lg bg-zinc-50 p-2.5"><ListChecks size={16} className="mx-auto text-violet-500 mb-1" /><p className="text-[11px] text-zinc-500">Request types</p><p className="text-sm font-semibold">{preview.request_types?.length || 0}</p></div>
                    <div className="rounded-lg bg-zinc-50 p-2.5"><Lifebuoy size={16} className="mx-auto text-blue-500 mb-1" /><p className="text-[11px] text-zinc-500">Workflow statuses</p><p className="text-sm font-semibold">{preview.distinct_statuses?.length || 0}</p></div>
                    <div className="rounded-lg bg-zinc-50 p-2.5"><UsersThree size={16} className="mx-auto text-emerald-500 mb-1" /><p className="text-[11px] text-zinc-500">Requesters</p><p className="text-sm font-semibold">as customers</p></div>
                  </div>
                  {preview.sample?.length > 0 && (
                    <div className="rounded-lg border border-zinc-200 overflow-hidden">
                      <p className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold px-3 pt-2">Sample</p>
                      <div className="divide-y divide-zinc-100 max-h-[200px] overflow-y-auto">
                        {preview.sample.map(s => (
                          <div key={s.key} className="flex items-center gap-2 px-3 py-1.5 text-xs min-w-0">
                            <span className="text-zinc-400 w-16 shrink-0">{s.key}</span>
                            <span className="flex-1 min-w-0 truncate text-zinc-700">{s.title}</span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500 shrink-0">{s.mapped_status}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <p className="text-[11px] text-zinc-400">A new Service Desk portal <span className="font-medium text-zinc-600">"{selected?.name}"</span> will be created with these requests, request types, requesters (as free customers), agents (matched by email), <span className="font-medium text-zinc-600">public + internal comments and attachments</span>. Statuses map to Open / In progress / Pending / Resolved / Closed. (Up to 5,000 requests; attachments over 25&nbsp;MB are skipped.)</p>
                </div>
                <DialogFooter className="mt-1 pt-3 border-t border-zinc-100">
                  <Button variant="outline" onClick={() => setStep('projects')}>Back</Button>
                  <Button onClick={startImport} data-testid="jsm-start-import"><DownloadSimple size={16} className="mr-1.5" /> Import to new portal</Button>
                </DialogFooter>
              </>
            )}
          </div>
        )}

        {/* Progress */}
        {step === 'progress' && (
          <div className="space-y-4 py-2" data-testid="jsm-step-progress">
            {job?.status === 'error' ? (
              <div className="text-center py-6">
                <WarningCircle size={40} weight="fill" className="text-red-500 mx-auto mb-2" />
                <p className="font-semibold text-zinc-900">Import failed</p>
                <p className="text-sm text-zinc-500 mt-1">{job.error}</p>
              </div>
            ) : job?.status === 'done' ? (
              <div className="text-center py-2" data-testid="jsm-import-done">
                <CheckCircle size={44} weight="fill" className="text-emerald-500 mx-auto mb-2" />
                <p className="font-semibold text-zinc-900">Import complete</p>
                <div className="grid grid-cols-2 gap-2 text-left mt-4 text-sm">
                  <Stat label="Requests imported" value={job.tickets_created} />
                  <Stat label="Request types" value={job.request_types_created} />
                  <Stat label="Customers created" value={job.customers_created} />
                  <Stat label="Agents matched" value={job.agents_matched} />
                  <Stat label="Comments imported" value={job.comments_created} />
                  <Stat label="Attachments imported" value={job.attachments_created} />
                  {job.attachments_skipped > 0 && <Stat label="Attachments skipped" value={job.attachments_skipped} />}
                </div>
              </div>
            ) : (
              <div className="py-4">
                <p className="text-sm text-zinc-600 mb-3 flex items-center gap-2"><CircleNotch size={16} className="animate-spin text-blue-600" /> {fetched && !processed ? `Fetching requests from Jira (${fetched})…` : processed ? `Importing ${processed}${total ? ` / ${total}` : ''}…` : 'Starting…'}</p>
                <div className="h-2 rounded-full bg-zinc-100 overflow-hidden"><div className="h-full bg-blue-600 transition-all duration-500" style={{ width: `${pct}%` }} /></div>
              </div>
            )}
            <DialogFooter>
              {job?.status === 'done' && job.target_portal_id ? (
                <>
                  <Button variant="outline" onClick={close}>Close</Button>
                  <Button onClick={() => { onOpenPortal?.(job.target_portal_id); close(); }} data-testid="jsm-open-portal"><ArrowRight size={16} className="mr-1.5" /> Configure portal</Button>
                </>
              ) : job?.status === 'error' ? (
                <Button variant="outline" onClick={() => setStep('projects')}>Try another project</Button>
              ) : (
                <Button variant="outline" onClick={close}>Run in background</Button>
              )}
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg bg-zinc-50 px-3 py-2">
      <p className="text-lg font-bold text-zinc-900">{value ?? 0}</p>
      <p className="text-[11px] text-zinc-500">{label}</p>
    </div>
  );
}
